export type LatLng = {
  lat: number;
  lng: number;
};

const EARTH_RADIUS_KM = 6371;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

/** Great-circle distance in kilometers between two lat/lng points. */
export const distanceInKm = (from: LatLng, to: LatLng): number => {
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
};

export const isWithinMaxDeliveryDistance = (
  restaurant: LatLng,
  delivery: LatLng,
  maxDistanceKm: number
): boolean => {
  if (!Number.isFinite(maxDistanceKm) || maxDistanceKm <= 0) {
    return false;
  }
  return distanceInKm(restaurant, delivery) <= maxDistanceKm;
};

export const geocodeAddress = (address: string): Promise<LatLng | null> => {
  return new Promise((resolve) => {
    if (!address || !window.google?.maps?.Geocoder) {
      resolve(null);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results: any[], status: string) => {
      if (status !== "OK" || !results?.[0]?.geometry?.location) {
        resolve(null);
        return;
      }
      const location = results[0].geometry.location;
      resolve({
        lat: typeof location.lat === "function" ? location.lat() : location.lat,
        lng: typeof location.lng === "function" ? location.lng() : location.lng,
      });
    });
  });
};

export const latLngFromGoogleLatLng = (location: any): LatLng | null => {
  if (!location) return null;
  const lat = typeof location.lat === "function" ? location.lat() : location.lat;
  const lng = typeof location.lng === "function" ? location.lng() : location.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return { lat, lng };
};
